import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { countryName, ISO_COUNTRY_CODES } from '@/lib/countries'
import type { JurisdictionOverrideAddRequest, Tripwire } from '@/types/jurisdictions'
import { overrideErrorMessage, TRIPWIRE_LABELS } from './overrideCopy'

interface Props {
    open: boolean
    onOpenChange: (open: boolean) => void
    matrixBlocked: readonly string[]
    activeCountries: readonly string[]
    onAdd: (body: JurisdictionOverrideAddRequest) => Promise<unknown>
}

const FIELD_CLASS =
    'border-input bg-background block h-9 w-full rounded-md border px-3 text-sm disabled:opacity-50'

export function AddOverrideDialog({
    open,
    onOpenChange,
    matrixBlocked,
    activeCountries,
    onAdd,
}: Props) {
    const [country, setCountry] = useState('')
    const [tripwire, setTripwire] = useState('')
    const [note, setNote] = useState('')
    const [showErrors, setShowErrors] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const [pending, setPending] = useState(false)

    const [wasOpen, setWasOpen] = useState(false)
    if (open !== wasOpen) {
        setWasOpen(open)
        if (open) {
            setCountry('')
            setTripwire('')
            setNote('')
            setShowErrors(false)
            setSubmitError(null)
        }
    }

    const matrixSet = new Set(matrixBlocked)
    const activeSet = new Set(activeCountries)
    const options = ISO_COUNTRY_CODES.map((code) => ({
        code,
        name: countryName(code),
        disabledReason: matrixSet.has(code)
            ? 'Already blocked by the matrix'
            : activeSet.has(code)
              ? 'Already has a provisional block'
              : null,
    })).sort((a, b) => a.name.localeCompare(b.name))

    const errors = {
        country: country ? null : 'Choose a country.',
        tripwire: tripwire ? null : 'Choose the tripwire that fired.',
        note: note.trim() ? null : 'Add a note saying why.',
    }
    const valid = !errors.country && !errors.tripwire && !errors.note

    function handleOpenChange(next: boolean) {
        if (pending) return
        onOpenChange(next)
    }

    async function submit() {
        setShowErrors(true)
        setSubmitError(null)
        if (!valid) return
        setPending(true)
        try {
            await onAdd({
                country_code: country,
                tripwire: Number(tripwire) as Tripwire,
                note: note.trim(),
            })
            onOpenChange(false)
        } catch (err) {
            setSubmitError(overrideErrorMessage(err))
        } finally {
            setPending(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Block a country</DialogTitle>
                    <DialogDescription>
                        The block takes effect within a minute at registration, entry and waitlist
                        signup. It stays until an MLRO lifts it; fold it into the jurisdiction
                        matrix within 14 days.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-1">
                    <Label htmlFor="jo-country">Country</Label>
                    <select
                        id="jo-country"
                        className={FIELD_CLASS}
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        aria-invalid={showErrors && !!errors.country}
                        aria-describedby={
                            showErrors && errors.country ? 'jo-country-error' : undefined
                        }
                    >
                        <option value="">Choose a country</option>
                        {options.map((o) => (
                            <option
                                key={o.code}
                                value={o.code}
                                disabled={o.disabledReason !== null}
                            >
                                {o.disabledReason
                                    ? `${o.name} (${o.code}) — ${o.disabledReason}`
                                    : `${o.name} (${o.code})`}
                            </option>
                        ))}
                    </select>
                    {showErrors && errors.country && (
                        <p id="jo-country-error" className="text-destructive text-xs">
                            {errors.country}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <Label htmlFor="jo-tripwire">Tripwire</Label>
                    <select
                        id="jo-tripwire"
                        className={FIELD_CLASS}
                        value={tripwire}
                        onChange={(e) => setTripwire(e.target.value)}
                        aria-invalid={showErrors && !!errors.tripwire}
                        aria-describedby={
                            showErrors && errors.tripwire ? 'jo-tripwire-error' : undefined
                        }
                    >
                        <option value="">Choose the tripwire that fired</option>
                        {([1, 2, 3] as const).map((t) => (
                            <option key={t} value={t}>
                                {`${t} · ${TRIPWIRE_LABELS[t]}`}
                            </option>
                        ))}
                    </select>
                    {showErrors && errors.tripwire && (
                        <p id="jo-tripwire-error" className="text-destructive text-xs">
                            {errors.tripwire}
                        </p>
                    )}
                </div>

                <div className="space-y-1">
                    <Label htmlFor="jo-note">Note</Label>
                    <textarea
                        id="jo-note"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What fired the tripwire, and where the evidence is (MLRO case, letter, email)."
                        className="border-input bg-background block min-h-[5rem] w-full rounded-md border p-2 text-sm"
                        aria-invalid={showErrors && !!errors.note}
                        aria-describedby={showErrors && errors.note ? 'jo-note-error' : undefined}
                    />
                    {showErrors && errors.note ? (
                        <p id="jo-note-error" className="text-destructive text-xs">
                            {errors.note}
                        </p>
                    ) : (
                        <p className="text-muted-foreground text-[11px]">
                            Stored verbatim in the audit log.
                        </p>
                    )}
                </div>

                {submitError && (
                    <p role="alert" className="text-destructive text-sm">
                        {submitError}
                    </p>
                )}

                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => handleOpenChange(false)}
                        disabled={pending}
                    >
                        Cancel
                    </Button>
                    <Button variant="destructive" onClick={submit} disabled={pending}>
                        {pending ? 'Blocking…' : 'Block country'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
